import { editorRecipientInputQuery, getImageURLQuery } from '@getaccept/editor-lib';
import { EditorBlockService } from '@getaccept/editor/src/api/services/editor-block.service';
import type { DocumentNotificationTransferMode } from '@getaccept/lib-shared/src/enums/document-notification-transfer-mode';
import { GraphQLFetchPolicy } from '@getaccept/lib-shared/src/graphql/graphql-fetch-policy';
import type { CreatePageDTO } from '@getaccept/lib-shared/src/types/create-page-dto';
import type { Document } from '@getaccept/lib-shared/src/types/Document';
import type { DocumentPage } from '@getaccept/lib-shared/src/types/document-page';
import type { DocumentRevision } from '@getaccept/lib-shared/src/types/document-revision';
import type { EditorRecipientInput } from '@getaccept/lib-shared/src/types/editor-recipient-input';
import type { Session } from '@getaccept/lib-shared/src/types/Session';
import type { UpdateDocumentPatch } from '@getaccept/lib-shared/src/types/update-document-patch';
import Axios from 'axios';
import type { EventLog } from '@getaccept/lib-shared/src/timeline/types/event-log';
import { EventName } from '@getaccept/lib-shared/src/enums/event-name';
import type { Filter } from '@getaccept/lib-shared/src/types/filter';
import type { DocumentListItem } from '@getaccept/lib-shared/src/types/document-list-item';
import type { UserDocumentAccess } from '@getaccept/lib-shared/src/documents/types/user-document-access';
import type { GraphQLErrors } from '@apollo/client/errors';
import type { DocumentStatus } from '@getaccept/lib-shared/src/enums/document-status';
import type { Recipient } from '@getaccept/lib-shared/src/types/Recipient';
import { apolloClientInternal } from '@getaccept/lib-shared/src/internal/apollo-client-internal';
import type { UploadFileUrl } from '../../../types/upload-file-url';
import type { PublishDocumentPayload } from '../../../document-view/types/publish-document-payload';
import type { PreviewToken } from '../../../document-edit/assembler/types/enums/preview-token';
import { inProduction } from '../../../common/helpers';
import { DocumentQueries } from '../graphql/document.queries';
import { DocumentMutations } from '../graphql/document.mutations';
import type { UpdatePageDTO } from '../dtos/update-page-dto';

export class DocumentService {
  private static readonly documentIdFormName = 'document-id';
  private static readonly saveDocumentPath = '/save/document';

  static async bulkAddTags(documentIds: string[], tagIds: string[]): Promise<string[]> {
    const { data } = await apolloClientInternal.mutate({
      mutation: DocumentQueries.addTagsMutation,
      variables: { documentIds, tagIds },
    });
    return data.addDocumentTags;
  }

  static async loadDocument(documentId: string): Promise<Document> {
    const {
      data: { document },
    } = await apolloClientInternal.query({
      query: DocumentQueries.documentQuery,
      variables: { documentId },
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });
    return document;
  }

  static async loadDocumentWithoutAttachments(documentId: string): Promise<Document> {
    const {
      data: { document },
    } = await apolloClientInternal.query({
      query: DocumentQueries.documentWithoutAttachments,
      variables: { documentId },
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });
    return document;
  }

  static async getParentDocumentId(documentId: string): Promise<string> {
    const {
      data: { document },
    } = await apolloClientInternal.query({
      query: DocumentQueries.documentParentIdQuery,
      variables: { documentId },
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });
    return document.parentDocumentId;
  }

  static async getReminderSettings(documentId: string): Promise<Partial<Document>> {
    const {
      data: { document },
    } = await apolloClientInternal.query({
      query: DocumentQueries.reminderSettingsQuery,
      variables: { documentId },
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });
    return document;
  }

  static async loadDocumentRevisions(documentId: string): Promise<DocumentRevision[]> {
    const {
      data: {
        document: { documentRevisions },
      },
    } = await apolloClientInternal.query({
      query: DocumentQueries.documentRevisionsQuery,
      variables: { documentId },
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });
    return documentRevisions;
  }

  static async loadEditorRecipientInput(documentId: string): Promise<EditorRecipientInput[]> {
    const {
      data: { editorRecipientInput },
    } = await apolloClientInternal.query({
      query: editorRecipientInputQuery,
      variables: { documentId },
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });
    return editorRecipientInput;
  }

  static async loadDocumentPage(documentId: string, id: string): Promise<DocumentPage> {
    const {
      data: { documentPage },
    } = await apolloClientInternal.query({
      query: DocumentQueries.loadDocumentPageQuery,
      variables: { documentId, id },
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });
    return documentPage;
  }

  static async publishDocument({ documentId, message }: PublishDocumentPayload): Promise<boolean> {
    const {
      data: { publishDocument },
    } = await apolloClientInternal.mutate({
      mutation: DocumentMutations.publishDocumentMutation,
      variables: { documentId, message },
    });
    return publishDocument;
  }

  static async loadPages(documentId: string): Promise<DocumentPage[]> {
    const {
      data: { document },
    } = await apolloClientInternal.query({
      query: DocumentQueries.documentPageQuery,
      variables: { documentId },
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });
    return document.pages;
  }

  static async discardChanges(documentId: string): Promise<boolean> {
    const {
      data: { discardChanges },
    } = await apolloClientInternal.mutate({
      mutation: DocumentMutations.discardChangesMutation,
      variables: { documentId },
    });
    return discardChanges;
  }

  static async loadPageThumbUrl(
    documentPageId: string,
    documentId: string
  ): Promise<Partial<DocumentPage>> {
    const {
      data: { documentPage },
    } = await apolloClientInternal.query({
      query: DocumentQueries.pageThumbUrl,
      variables: { id: documentPageId, documentId },
      fetchPolicy: GraphQLFetchPolicy.CacheFirst,
    });
    return documentPage;
  }

  static async createSampleDocument(session: Session, templateId?: string): Promise<string> {
    const payload = new FormData();
    let fallbackTempId = '5qdv4u5vrm2x';
    if (!inProduction(window)) {
      fallbackTempId = 'n2f9n5v5';
    }
    payload.append(this.documentIdFormName, templateId ? templateId : fallbackTempId);
    const {
      data: { document_id, error },
    } = await Axios.post('/get/sample/document', payload);
    if (error) {
      throw new Error(error);
    }
    if (!session) {
      return;
    }
    if (document_id) {
      let firstRoleId;
      if (templateId !== fallbackTempId) {
        const document: Document = await DocumentService.loadDocument(document_id);
        const firstRole = document?.recipients?.find(recipient => recipient.roleName !== '');
        firstRoleId = firstRole?.id;
      }
      const body = new FormData();
      body.append('action', 'save');
      body.append(this.documentIdFormName, document_id);
      body.append('recipient-email', session.email || '');
      body.append('recipient-first_name', session.first_name || '');
      body.append('recipient-last_name', session.last_name || '');
      body.append('recipient-role', 'signer');
      if (firstRoleId) {
        body.append('recipient-id', firstRoleId);
      }
      await Axios.post('/save/recipient', body);
    }

    return document_id;
  }

  static async getPreviewToken(documentId: string): Promise<PreviewToken> {
    const {
      data: { previewToken },
    } = await apolloClientInternal.query({
      query: DocumentQueries.documentPreviewTokenQuery,
      variables: { documentId },
      fetchPolicy: GraphQLFetchPolicy.NoCache,
    });
    return previewToken;
  }

  static async createDocument(documentType: string): Promise<string> {
    const body = new FormData();
    body.append('action', 'data');
    body.append('document-type', documentType);
    const {
      data: { document_id },
    } = await Axios.post(this.saveDocumentPath, body);
    return document_id;
  }

  static async loadPageNums(documentId: string): Promise<Partial<DocumentPage>[]> {
    const {
      data: {
        document: { pages },
      },
    } = await apolloClientInternal.query({
      query: DocumentQueries.pageNumQuery,
      variables: { documentId },
      fetchPolicy: GraphQLFetchPolicy.NoCache,
    });
    return pages;
  }

  static async loadTemplateResourceDetails(contentId: string): Promise<Partial<Document>> {
    const {
      data: { document },
    } = await apolloClientInternal.query({
      query: DocumentQueries.templateResourceDetailsQuery,
      variables: { contentId },
    });
    return document;
  }

  static async loadDocumentPreviewImageUrl(documentId: string): Promise<string> {
    const {
      data: { documentPreviewImageUrl },
    } = await apolloClientInternal.query({
      query: DocumentQueries.documentPreviewImageUrl,
      variables: { documentId },
    });
    return documentPreviewImageUrl;
  }

  static async createPage(payload: CreatePageDTO) {
    const {
      data: { addDocumentPage },
    } = await apolloClientInternal.mutate({
      mutation: DocumentMutations.createPage,
      variables: { ...EditorBlockService.stripUnwantedProperties(structuredClone(payload)) },
    });
    return addDocumentPage;
  }

  static async deletePage(
    documentId: string,
    documentPageId: string,
    deletedPricingTableIds: string[]
  ): Promise<boolean> {
    const {
      data: { deleteDocumentPage },
    } = await apolloClientInternal.mutate({
      mutation: DocumentMutations.deleteDocumentPageMutation,
      variables: { documentId, documentPageId, deletedPricingTableIds },
    });
    return deleteDocumentPage;
  }

  static async delete(documentId: string): Promise<boolean> {
    const body = new FormData();

    body.append('action', 'delete');
    body.append(this.documentIdFormName, documentId);

    const { data } = await Axios.post('/save/board', body);
    if (data.error) {
      throw new Error(data.error);
    }
    if (data.status !== 1) {
      throw new Error(data);
    }
    return !!data.status;
  }

  static async deleteMany(ids: string[]): Promise<boolean> {
    const { data } = await apolloClientInternal.mutate({
      mutation: DocumentMutations.deleteDocumentsMutation,
      variables: { ids },
    });
    return data;
  }

  static async updatePage(dto: UpdatePageDTO): Promise<DocumentPage> {
    const { data } = await apolloClientInternal.mutate({
      mutation: DocumentMutations.updateDocumentPageMutation,
      variables: dto,
    });
    return data.updateDocumentPage;
  }

  static async useTemplate(
    documentId: string,
    templateId: string,
    pageOffset: number
  ): Promise<Document> {
    const {
      data: { implementTemplateInDocument },
    } = await apolloClientInternal.mutate({
      mutation: DocumentMutations.implementTemplateInDocumentMutation,
      variables: {
        documentId,
        templateId,
        pageOffset,
      },
    });
    return implementTemplateInDocument;
  }

  static async uploadFiles(uploadFileUrls: UploadFileUrl[], files: File[]): Promise<void> {
    await Promise.all(
      uploadFileUrls.map(async ({ uploadUrl }: UploadFileUrl, index: number) =>
        Axios.put(uploadUrl, files[index])
      )
    );
  }

  static async loadExternalEditorId(documentId: string): Promise<string> {
    const {
      data: {
        document: { externalEditorId },
      },
    } = await apolloClientInternal.query({
      query: DocumentQueries.externalEditorIdQuery,
      variables: { documentId },
      fetchPolicy: GraphQLFetchPolicy.NoCache,
    });
    return externalEditorId;
  }

  static async sortDocumentPages(
    documentId: string,
    documentPageIds: string[],
    pageNums: number[]
  ): Promise<DocumentPage[]> {
    const { data } = await apolloClientInternal.mutate({
      mutation: DocumentMutations.sortDocumentPagesMutation,
      variables: {
        documentId,
        documentPageIds,
        pageNums,
      },
    });
    return data.sortDocumentPages;
  }

  static async sealDocument(documentId: string, sendDocument: boolean): Promise<void> {
    await apolloClientInternal.mutate({
      mutation: DocumentMutations.sealDocumentMutation,
      variables: {
        documentId,
        sendDocument,
      },
    });
  }

  static async deleteTemplate(id: string): Promise<void> {
    const body: FormData = new FormData();
    body.append('action', 'delete');
    body.append(this.documentIdFormName, id);
    await Axios.post('/save/board', body);
  }

  static async updateSignature(signature: string): Promise<void> {
    await apolloClientInternal.mutate({
      mutation: DocumentMutations.updateSignatureMutation,
      variables: {
        signature,
      },
    });
  }

  static async loadDocuments({
    documentFilter,
    userIds,
    orderBy,
    limit,
    pageNumber,
    tagIds,
    withoutTags,
  }: Filter): Promise<DocumentListItem[]> {
    const excludeTags: boolean = !tagIds?.length || withoutTags;
    const {
      data: { getDocuments },
    } = await apolloClientInternal.query({
      query: DocumentQueries.documentsQuery,
      variables: {
        documentFilter,
        userIds,
        orderBy: orderBy?.sortBy ? `${orderBy.sortBy}:${orderBy.sortOrder}` : undefined,
        limit,
        offset: pageNumber * limit,
        tagIds: excludeTags ? undefined : tagIds,
        withoutTags,
      },
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });
    return getDocuments;
  }

  static async documentUpdated(documentId: string): Promise<boolean> {
    const {
      data: { documentUpdated },
    } = await apolloClientInternal.mutate({
      mutation: DocumentMutations.documentUpdatedMutation,
      variables: { documentId },
    });
    return documentUpdated;
  }
  static async getDocumentStatus(documentId: string): Promise<DocumentStatus> {
    const {
      data: { document },
    } = await apolloClientInternal.query({
      query: DocumentQueries.DocumentStatusQuery,
      variables: { documentId },
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });
    return document.status;
  }

  static updateDocument = async (
    patch: UpdateDocumentPatch,
    documentId: string,
    transferMode?: DocumentNotificationTransferMode
  ): Promise<boolean> => {
    const {
      data: { updateDocument },
    } = await apolloClientInternal.mutate({
      mutation: DocumentMutations.updateDocumentMutation,
      variables: {
        patch,
        settings: { transferMode },
        documentId,
      },
    });
    return updateDocument;
  };

  static async signDocument(documentId: string): Promise<boolean> {
    const body: FormData = new FormData();
    body.append('action', 'sign');
    body.append(this.documentIdFormName, documentId);

    const { data } = await Axios.post('/save/document', body);
    if (data.error) {
      throw new Error(data.error);
    }
    return data.status === 1;
  }

  static async saveSignature(
    documentId: string,
    signature: string,
    saveSignature: boolean
  ): Promise<boolean> {
    const body: FormData = new FormData();
    body.append('action', 'signature');
    body.append('signature', signature);
    body.append('save_user_signature', (+saveSignature).toString());
    body.append(this.documentIdFormName, documentId);

    const { data } = await Axios.post(this.saveDocumentPath, body);
    if (data.error) {
      throw new Error(data.error);
    }
    return data.status === 1;
  }

  static updateAndLogSelfSignSignature = async (
    signature: string,
    signatureBase64Svg: string,
    documentId: string,
    replaceUserSignature: boolean,
    nodeIds: string[]
  ): Promise<boolean> => {
    const {
      data: { updateAndLogSelfSignSignature },
    } = await apolloClientInternal.mutate({
      mutation: DocumentMutations.updateAndLogSelfSignSignatureMutation,
      variables: {
        signature,
        signatureBase64Svg,
        documentId,
        replaceUserSignature,
        nodeIds,
      },
    });
    return updateAndLogSelfSignSignature;
  };

  static updateDocuments = async (
    patch: UpdateDocumentPatch,
    documentIds: string[],
    transferMode?: DocumentNotificationTransferMode
  ): Promise<boolean> => {
    const {
      data: { updateDocuments },
    } = await apolloClientInternal.mutate({
      mutation: DocumentMutations.updateDocuments,
      variables: {
        patch: {
          ...patch,
          isReminderSending: +patch.isReminderSending,
          isSmsReminder: +patch.isSmsReminder,
          reminderRepeat: +patch.reminderRepeat,
        },
        settings: { transferMode },
        documentIds,
      },
    });
    return updateDocuments;
  };

  static async download(documentId: string): Promise<void> {
    await apolloClientInternal.mutate({
      mutation: DocumentMutations.download,
      variables: {
        documentId,
      },
    });
  }

  static async downloadDocuments(documentIds: string[]): Promise<boolean> {
    const {
      data: { batchDownloadDocuments },
    } = await apolloClientInternal.mutate({
      mutation: DocumentMutations.downloadDocuments,
      variables: { documentIds },
    });
    return batchDownloadDocuments;
  }

  static async loadPartiallySignedFields(documentId: string): Promise<Partial<EventLog[]>> {
    const {
      data: { eventLogs },
    } = await apolloClientInternal.query({
      query: DocumentQueries.eventLogQuery,
      variables: { documentId, eventName: EventName.PartiallySigned },
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });
    return eventLogs;
  }

  static async loadSignedEvents(documentId: string): Promise<Partial<EventLog[]>> {
    const {
      data: { eventLogs },
    } = await apolloClientInternal.query({
      query: DocumentQueries.eventLogQuery,
      variables: { documentId, eventName: EventName.Signed },
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });
    return eventLogs;
  }

  static async loadUserDocumentAccess(documentId: string): Promise<UserDocumentAccess> {
    const {
      data: { userDocumentAccess },
    } = await apolloClientInternal.query({
      query: DocumentQueries.UserDocumentAccessQuery,
      variables: { documentId },
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
      context: {
        errorMiddleware: (graphQLErrors: GraphQLErrors) =>
          graphQLErrors.filter(error => !error.message.includes('No such entity')),
      },
    });

    return userDocumentAccess;
  }

  static async accessToPrivateDocument(documentId: string): Promise<boolean> {
    const {
      data: { document },
    } = await apolloClientInternal.query({
      query: DocumentQueries.accessToPrivateDocument,
      variables: { documentId },
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
      context: {
        errorMiddleware: (graphQLErrors: GraphQLErrors) =>
          graphQLErrors.filter(
            error =>
              !error.message.includes('Access denied for documents') ||
              !error.message.includes('Documents not found')
          ),
      },
    });

    return !!document.id;
  }

  static async notifyChangesDiscarded(documentId: string): Promise<void> {
    await apolloClientInternal.mutate({
      mutation: DocumentMutations.triggerEditAfterSentEvent,
      variables: { isActive: false, documentId },
    });
  }

  static async getDocumentQES(id: string): Promise<boolean> {
    const {
      data: {
        getDocument: { settings },
      },
    } = await apolloClientInternal.query({
      query: DocumentQueries.documentQualifiedElectronicSignature,
      variables: {
        id,
      },
    });
    return settings?.qualifiedElectronicSignature || false;
  }

  static async updateDocumentQES(id: string, value: boolean): Promise<void> {
    await apolloClientInternal.mutate({
      mutation: DocumentMutations.updateDocumentQualifiedElectronicSignature,
      variables: {
        documentId: id,
        payload: {
          settings: {
            qualifiedElectronicSignature: value,
          },
        },
      },
    });
  }

  static async loadRecipients(documentId: string): Promise<Recipient[]> {
    const {
      data: {
        document: { recipients },
      },
    } = await apolloClientInternal.query({
      query: DocumentQueries.recipientsDetailed,
      variables: { documentId },
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });
    return recipients;
  }

  static async getEditorImageURL(
    id: string,
    height?: string
  ): Promise<{ url: string; externalId: string; blurhash: string }> {
    const {
      data: { image },
    } = await apolloClientInternal.query({
      query: getImageURLQuery,
      variables: { id, height },
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });

    return {
      url: image?.url || '',
      externalId: image?.externalId || undefined,
      blurhash: image?.blurhash || '',
    };
  }
}
